import React from 'react';
import LegalShell from './legal/LegalShell';

const H2 = ({ n, children }) => (
  <h2 className="text-xl font-extrabold text-white mt-8 mb-2">
    <span style={{ color: '#00D26A' }}>{n}.</span> {children}
  </h2>
);
const H3 = ({ n, children }) => (
  <h3 className="text-base font-bold text-white mt-5 mb-1">
    <span className="text-zinc-500">{n}</span> {children}
  </h3>
);

export default function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" lastUpdated="20 April 2026" version="v1 DRAFT">
      <p>
        This Privacy Policy explains how Flipping Friends Ltd (&ldquo;<strong>we</strong>&rdquo;,
        &ldquo;<strong>us</strong>&rdquo;) collects, uses and protects your personal data. It is
        written to be compliant with the{' '}
        <strong>UK General Data Protection Regulation (UK GDPR)</strong> and the{' '}
        <strong>Data Protection Act 2018</strong>.
      </p>

      <H2 n={1}>Who is the controller?</H2>
      <p>
        Flipping Friends Ltd is the &ldquo;data controller&rdquo; of your personal data. Contact:{' '}
        <a href="mailto:marcus@flippingfriends.co.uk" className="text-[#00D26A] underline">
          marcus@flippingfriends.co.uk
        </a>
        . Our Information Commissioner&apos;s Office (ICO) registration number will be published
        here once issued.
      </p>

      <H2 n={2}>What we collect and why</H2>
      <H3 n="2.1">Account data</H3>
      <p>
        When you sign in with Google we receive: <em>email address, full name, profile picture URL,
        Google account identifier</em>. We use this to create and maintain your account and to
        email you about your account.
      </p>
      <H3 n="2.2">Usage data you create</H3>
      <p>
        We store: your barcode scans, submitted prices, wanted-list entries, match history,
        outreach activity, data donations, username and session activity. This is used to run
        the service, calculate earnings, protect against fraud, and power Community Match.
      </p>
      <H3 n="2.3">Technical data</H3>
      <p>
        Our servers log the IP address, browser user-agent, and basic request metadata of each
        request. This is used for security, abuse detection and diagnostics, and retained for
        up to 90 days unless required longer by law.
      </p>
      <H3 n="2.4">Contact &amp; communications</H3>
      <p>
        If you message us or complete a form, we keep a record of your message, email address,
        and any other details you provided, so we can respond and for audit purposes.
      </p>
      <H3 n="2.5">Payment data <span className="text-xs text-zinc-500">(future)</span></H3>
      <p>
        When payments are enabled we will use a regulated payment processor (e.g. Stripe) who
        will collect card details directly. We will receive only the payment outcome (success
        / fail, last-4 of card, amount, currency) — never the full card number.
      </p>

      <H2 n={3}>Lawful bases for processing</H2>
      <p>We rely on the following UK GDPR lawful bases:</p>
      <ul className="list-disc pl-6 space-y-1 text-zinc-300">
        <li>
          <strong>Contract (Art. 6(1)(b)):</strong> to provide the service you sign up for —
          account creation, earnings ledger, match notifications, payouts.
        </li>
        <li>
          <strong>Legitimate interests (Art. 6(1)(f)):</strong> fraud prevention, security,
          anonymised product analytics, service improvement. You can object at any time — see §7.
        </li>
        <li>
          <strong>Consent (Art. 6(1)(a)):</strong> marketing emails (e.g. product-launch
          announcements beyond transactional service emails). You can withdraw consent via the
          unsubscribe link in every such email.
        </li>
        <li>
          <strong>Legal obligation (Art. 6(1)(c)):</strong> retaining records required for
          accounting, tax, or regulatory reasons.
        </li>
      </ul>

      <H2 n={4}>Who we share data with (processors)</H2>
      <p>We use the following processors — all bound by data-processing agreements:</p>
      <ul className="list-disc pl-6 space-y-1 text-zinc-300">
        <li>
          <strong>Google LLC</strong> — OAuth sign-in (identity verification only)
        </li>
        <li>
          <strong>Emergent Labs</strong> — hosting platform for our application
        </li>
        <li>
          <strong>MongoDB Atlas</strong> — database storage (UK / EU region)
        </li>
        <li>
          <strong>Resend</strong> — transactional email delivery
        </li>
        <li>
          <strong>Mailchimp (Intuit Inc.)</strong> — waitlist list storage (automations are
          currently paused; Mailchimp is used purely as a storage backend)
        </li>
        <li>
          <strong>Stripe <span className="text-xs text-zinc-500">(future)</span></strong> — payment
          processing and payouts, when enabled
        </li>
      </ul>
      <p>
        Some of these processors may transfer data outside the UK. When this happens we rely on
        the UK International Data Transfer Agreement (IDTA) or equivalent Standard Contractual
        Clauses, plus the EU/US Data Privacy Framework where applicable.
      </p>
      <p>
        We <strong>do not sell your personal data</strong> to anyone. We do not share it with
        advertisers. Aggregated, anonymised statistics (e.g. &ldquo;63 barcodes validated&rdquo;)
        may be shown publicly, but individuals cannot be identified from them.
      </p>

      <H2 n={5}>Cookies &amp; similar technologies</H2>
      <p>
        Flipping Friends uses a <strong>single essential cookie</strong>: <code>session_token</code>,
        an HTTP-only, Secure, SameSite=None cookie that keeps you signed in for up to 7 days. No
        tracking, advertising or analytics cookies are used at present. Because this cookie is
        strictly necessary for the service to function, no consent banner is required under UK
        ePrivacy rules. If this changes in future (e.g. we add analytics) we will add a consent
        banner and update this policy first.
      </p>

      <H2 n={6}>How long we keep data</H2>
      <ul className="list-disc pl-6 space-y-1 text-zinc-300">
        <li><strong>Account data:</strong> for the lifetime of your account, plus 6 years after closure for accounting compliance.</li>
        <li><strong>Scan &amp; price data:</strong> retained indefinitely in anonymised form as part of the aggregated database.</li>
        <li><strong>Session cookies:</strong> up to 7 days.</li>
        <li><strong>Server request logs:</strong> up to 90 days.</li>
        <li><strong>Contact enquiries:</strong> up to 2 years after resolution.</li>
        <li><strong>Marketing email lists:</strong> until you unsubscribe, then 30 days to complete deletion.</li>
      </ul>

      <H2 n={7}>Your rights</H2>
      <p>
        Under the UK GDPR you have the right to: access your data; correct inaccurate data;
        erase data (&ldquo;right to be forgotten&rdquo;); restrict processing; data
        portability (get a machine-readable export); object to processing based on legitimate
        interests; withdraw consent at any time; complain to the Information Commissioner&apos;s
        Office (<a href="https://ico.org.uk" className="text-[#00D26A] underline">ico.org.uk</a>,
        0303 123 1113).
      </p>
      <p>
        To exercise any right, email{' '}
        <a href="mailto:marcus@flippingfriends.co.uk" className="text-[#00D26A] underline">
          marcus@flippingfriends.co.uk
        </a>
        . We respond within one month.
      </p>

      <H2 n={8}>Security</H2>
      <p>
        We protect your data with: TLS encryption of all traffic; Google OAuth-based sign-in
        (no passwords stored on our system); MongoDB access restricted to our backend; IP
        allowlisting of the production database; session expiry; rate-limiting on sensitive
        endpoints. No system is 100% secure — if we suffer a reportable breach we will notify
        you and the ICO within 72 hours as required by law.
      </p>

      <H2 n={9}>Children</H2>
      <p>
        Flipping Friends is for users aged <strong>18 and over</strong>. We do not knowingly
        collect data from anyone under 18. If we become aware we have collected such data we
        will delete it promptly.
      </p>

      <H2 n={10}>Changes to this Policy</H2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be notified
        by email to account holders and by a banner on the website. The &ldquo;Last updated&rdquo;
        date at the top of this page always reflects the current version.
      </p>

      <H2 n={11}>Contact us</H2>
      <p>
        For any privacy question or to exercise any right, email{' '}
        <a href="mailto:marcus@flippingfriends.co.uk" className="text-[#00D26A] underline">
          marcus@flippingfriends.co.uk
        </a>
        .
      </p>
    </LegalShell>
  );
}
